import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Eye, CheckCircle, AlertCircle, Clock, Mail, FolderOpen, X, MessageSquare, ThumbsUp, User, Play, Users } from 'lucide-react';
import { getToken } from '../auth/authConfig';

interface Employee {
  id: string;
  name: string;
  role: string;
  department: string;
  lastDay: string;
  daysRemaining: number;
}

interface DataSource {
  id: string;
  name: string;
  date: string;
  status: 'ingested' | 'cannot-access' | 'pending';
  duplicateVersions?: number;
  coreFlag: 'core' | 'nice-to-have';
  type: 'sharepoint' | 'email';
  employeeId: string;
}

const DocumentAccess: React.FC = () => {
  const { ktId } = useParams();
  const navigate = useNavigate();
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<'sharepoint' | 'email' | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageType, setSuccessMessageType] = useState<'employee' | 'manager'>('employee');
  const [overAllData, setOverAllData] = useState({})
  const [loading, setLoading] = useState(false);

  const [employees,setEmployees] = useState<Employee[]>([
  
  ]);

  const [allDocuments] = useState<DataSource[]>([
    // Sarah Johnson's documents
   
  ]);

  const fetchDocument = async () => {

    try{
      setLoading(true);
      const baseURL = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/document-access/overview`
      const token = await getToken()
      const response = await fetch(baseURL, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "oidc-token" : token
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      const employeeData = data; // Assuming the API returns an object with a 'role' property
      console.log("Role data fetched successfully:", employeeData["employeeStatuses"]);
      setEmployees(employeeData["employeeStatuses"])
      setOverAllData(employeeData["overallProgress"]);
      // setRole(data.role); // Assuming the API returns an object with a 'role' property
      console.log("Role fetched successfully:", data);
      setLoading(false);

    }
    catch (error) {
      console.error("Error fetching role:", error);
      // Handle error appropriately
      setLoading(false);

    }
  }

  React.useEffect(() => {
    fetchDocument();
  }, []);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ingested':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cannot-access':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ingested':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cannot-access':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCoreFlag = (flag: string) => {
    return flag === 'core' 
      ? 'bg-blue-100 text-blue-800 border-blue-200'
      : 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const getEmployeeDocuments = (employeeId: string, type?: 'sharepoint' | 'email') => {
    let docs = allDocuments.filter(doc => doc.employeeId === employeeId);
    if (type) {
      docs = docs.filter(doc => doc.type === type);
    }
    return docs;
  };

  const getEmployeeDocumentStats = (employeeId: string) => {
    const docs = getEmployeeDocuments(employeeId);
    const sharepointDocs = docs.filter(doc => doc.type === 'sharepoint');
    const emailDocs = docs.filter(doc => doc.type === 'email');
    
    return {
      sharepoint: {
        total: sharepointDocs.length,
        ingested: sharepointDocs.filter(doc => doc.status === 'ingested').length,
        cannotAccess: sharepointDocs.filter(doc => doc.status === 'cannot-access').length,
        pending: sharepointDocs.filter(doc => doc.status === 'pending').length
      },
      email: {
        total: emailDocs.length,
        ingested: emailDocs.filter(doc => doc.status === 'ingested').length,
        cannotAccess: emailDocs.filter(doc => doc.status === 'cannot-access').length,
        pending: emailDocs.filter(doc => doc.status === 'pending').length
      }
    };
  };

 

  const handleViewEmployeeDocuments = (employee: Employee) => {
    setSelectedEmployee(employee);
    setShowDocumentModal(true);
  };



  const getDocumentsByType = (type: 'sharepoint' | 'email') => {
    if (!selectedEmployee) return [];
    return getEmployeeDocuments(selectedEmployee.id, type);
  };

  // Calculate overall progress
  const totalDocs = allDocuments.length;
  const ingestedDocs = allDocuments.filter(doc => doc.status === 'ingested').length;
  const progressPercentage = Math.round((ingestedDocs / totalDocs) * 100);

  if (loading) {
    return (
      <div
        role="status"
        style={{
          position: "absolute",
          left: "0px",
          right: "0px",
          top: "0px",
          bottom: "0px",
          background: "rgba(0,0,0,0.8)",
          zIndex: "99999",
          display: "grid",
          alignContent:"center"
          ,justifyContent: "center",
          justifyItems: "center",
          alignItems: "center",
        }}
      >
        <svg
          aria-hidden="true"
          className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-purple-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="pt-4 text-white">Loading Document Access Dashboad, Please wait...</span>

      </div>
    );
  }

  return (
    <div className="p-6 welcome-screen">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">
              {successMessageType === 'employee' 
                ? 'Employee review request sent successfully!' 
                : 'Manager review request sent successfully!'}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg">
            <FolderOpen className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Document Access Confirmation</h1>
            <p className="text-gray-600 mt-1">Review document access status for all employees</p>
          </div>
        </div>

        {/* Overall Progress Overview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Overall Document Ingestion Progress</h2>
            <span className="text-2xl font-bold text-purple-600">{overAllData.percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
            <div 
              className="bg-purple-600 h-3 rounded-full transition-all duration-300"
              style={{ width: `${overAllData.percentage}%` }}
            ></div>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-green-600">{overAllData.successCount}</p>
              <p className="text-sm text-gray-600">Successfully Ingested</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {overAllData.failCount}
              </p>
              <p className="text-sm text-gray-600">Cannot Access</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">
              {overAllData.pendingCount}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Documents Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">Employee Document Status</h2>
          <p className="text-gray-600 mt-1">Review document access status by employee</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Day
                </th>
                {/* <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SharePoint Docs
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Docs
                </th> */}
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Overall Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {employees && employees.length>0&& employees.map((employee) => {
                const stats = getEmployeeDocumentStats(employee.id);
                const totalEmployeeDocs = stats.sharepoint.total + stats.email.total;
                const totalIngested = stats.sharepoint.ingested + stats.email.ingested;
                const employeeProgress = totalEmployeeDocs > 0 ? Math.round((totalIngested / totalEmployeeDocs) * 100) : 0;
                
                return (
                  <tr key={employee.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                          <div className="text-sm text-gray-500">{employee.roleForKT}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">{employee.department}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <span className="text-sm text-gray-900">{employee.lastDay}</span>
                        <div className={`text-xs ${employee.daysRemaining <= 7 ? 'text-red-600' : 'text-gray-500'}`}>
                          {employee.daysRemaining} days remaining
                        </div>
                      </div>
                    </td>
                    {/* <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-900">{stats.sharepoint.total}</div>
                        <div className="text-xs text-gray-500">
                          <span className="text-green-600">{stats.sharepoint.ingested}</span> / 
                          <span className="text-red-600 ml-1">{stats.sharepoint.cannotAccess}</span> / 
                          <span className="text-yellow-600 ml-1">{stats.sharepoint.pending}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-center">
                        <div className="text-sm font-medium text-gray-900">{stats.email.total}</div>
                        <div className="text-xs text-gray-500">
                          <span className="text-green-600">{stats.email.ingested}</span> / 
                          <span className="text-red-600 ml-1">{stats.email.cannotAccess}</span> / 
                          <span className="text-yellow-600 ml-1">{stats.email.pending}</span>
                        </div>
                      </div>
                    </td> */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="w-16 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${employee.overallStatus}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{employee.overallStatus}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                      style={{backgroundColor:"#bb1ccc"}}
                        onClick={() => handleViewEmployeeDocuments(employee)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}

              {employees.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  <div className="text-center py-12">
                      <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        No Employee Available
                      </p>
                      <p className="text-gray-400">
                        Try adjusting your search or filters
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    
      {showDocumentModal && selectedEmployee && (
        <div style={{zIndex:"99999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEmployee.name}</h2>
                  <p className="text-gray-600">{selectedEmployee.role} • {selectedEmployee.department}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-8">
           
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Email Documents</h3>
                </div>
                <div className="space-y-4">
                  {getDocumentsByType('email').map((doc) => (
                    <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">{doc.name}</h4>
                          <p className="text-sm text-gray-500">Date: {doc.date}</p>
                          {doc.duplicateVersions && (
                            <p className="text-xs text-orange-600 mt-1">
                              {doc.duplicateVersions} duplicate versions found
                            </p>
                          )}
                        </div>
                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(doc.status)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc.status)}`}>
                            {doc.status === 'ingested' ? 'Successfully Ingested' : 
                             doc.status === 'cannot-access' ? 'Cannot Access' : 'Pending IT'}
                          </span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getCoreFlag(doc.coreFlag)}`}>
                          {doc.coreFlag === 'core' ? 'Core' : 'Nice-to-Have'}
                        </span>
                      </div>
                      
                      {doc.status === 'cannot-access' && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <Mail className="w-4 h-4 text-red-600 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-red-800">Access Issue</p>
                              <p className="text-sm text-red-700">Email sent to Manager for coordination with employee/IT</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Review & Update Section */}
              {/* <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Review & Update</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-3 mb-4">
                      <MessageSquare className="w-5 h-5 text-blue-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Employee Review</h4>
                        <p className="text-gray-600 text-sm mb-4">
                          Employee can review and make updates, such as marking an older version as the source of truth.
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={handleRequestEmployeeReview}
                      className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    >
                      Request Employee Review
                    </button>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-3 mb-4">
                      <ThumbsUp className="w-5 h-5 text-green-600 mt-1" />
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Manager Review</h4>
                        <p className="text-gray-600 text-sm mb-4">
                          Manager can review the document list and make necessary updates or corrections.
                        </p>
                      </div>
                    </div>
                    <button 
                      onClick={handleRequestManagerReview}
                      className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                    >
                      Request Manager Review
                    </button>
                  </div>
                </div>
              </div> */}

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-6  border-gray-200">
                <button
                  onClick={() => setShowDocumentModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentAccess;